import { useWebSocketContext } from '@/context/websocket-context';
import type { WebSocketMessage } from '@/hooks/use-websocket.hook';
import { useOrganization, useUser } from '@clerk/react';
import { useEffect, useMemo, useState } from 'react';

/**
 * Who on my team has the app open right now.
 *
 * The roster comes from cleanse-api (`core/presence.py`) over the SAME unified
 * WebSocket the rest of the app uses — presence means "has a tab open", not
 * "logged in at some point", and it goes stale on its own if a browser dies
 * without a clean close (the server ages members out on a 75s TTL fed by the
 * existing 30s heartbeat).
 *
 * The socket carries user IDs only. Names and avatars are joined here from the
 * Clerk membership list, so no profile data is duplicated over the wire.
 *
 * Tab-leader note: only the leader tab holds the socket, but every tab mounts
 * this hook and receives the roster through the provider's BroadcastChannel
 * fan-out, so followers stay in sync without their own connection.
 */

export type PresenceMember = {
  userId: string;
  name: string;
  email: string;
  imageUrl: string | null;
  online: boolean;
  isSelf: boolean;
};

/** Re-ask for the roster periodically. The server also pushes on every
 *  join/leave; this only covers a push missed across a reconnect. */
const REFRESH_INTERVAL_MS = 45_000;

export function usePresence(): {
  members: PresenceMember[];
  onlineCount: number;
  isReady: boolean;
} {
  const { subscribe, sendMessage, isConnected } = useWebSocketContext();
  const { user } = useUser();
  const { organization, memberships } = useOrganization({
    memberships: { pageSize: 100, keepPreviousData: true, infinite: true },
  });
  const [onlineIds, setOnlineIds] = useState<string[]>([]);

  // Page through the whole roster: an org larger than one page would otherwise
  // show teammates as missing rather than offline.
  useEffect(() => {
    if (!memberships?.hasNextPage || memberships.isFetching) return;
    memberships.fetchNext?.();
  }, [memberships?.hasNextPage, memberships?.isFetching, memberships]);

  useEffect(() => {
    return subscribe('presence', (message: WebSocketMessage) => {
      if (message.type !== 'presence') return;
      // Ignore a roster for an org we are no longer viewing (org switch races).
      if (organization?.id && message.organization_id !== organization.id)
        return;
      setOnlineIds(message.online ?? []);
    });
  }, [subscribe, organization?.id]);

  useEffect(() => {
    if (!isConnected) {
      // Don't keep painting a stale roster over a dead socket.
      setOnlineIds([]);
      return;
    }
    sendMessage({ type: 'presence' });
    const timer = setInterval(
      () => sendMessage({ type: 'presence' }),
      REFRESH_INTERVAL_MS
    );
    return () => clearInterval(timer);
  }, [isConnected, sendMessage, organization?.id]);

  const members = useMemo(() => {
    const online = new Set(onlineIds);
    const rows: PresenceMember[] = [];
    for (const membership of memberships?.data ?? []) {
      const pub = membership.publicUserData;
      if (!pub?.userId) continue;
      rows.push({
        userId: pub.userId,
        name:
          [pub.firstName, pub.lastName].filter(Boolean).join(' ') ||
          pub.identifier ||
          'Teammate',
        email: pub.identifier ?? '',
        imageUrl: pub.imageUrl ?? null,
        // The current user is online whenever this tab has a live socket —
        // their own entry must not depend on the roster round-trip landing.
        online: pub.userId === user?.id ? isConnected : online.has(pub.userId),
        isSelf: pub.userId === user?.id,
      });
    }
    // Online first, then self, then alphabetical — the useful ones stay in the
    // visible part of a truncated avatar stack.
    return rows.sort((a, b) => {
      if (a.online !== b.online) return a.online ? -1 : 1;
      if (a.isSelf !== b.isSelf) return a.isSelf ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [memberships?.data, onlineIds, user?.id, isConnected]);

  return {
    members,
    onlineCount: members.filter((m) => m.online).length,
    // A solo org has nothing to show — the strip hides itself rather than
    // rendering a one-avatar "team".
    isReady: members.length > 1,
  };
}
